/*
 * np-offer-render.js — cookie-sticky offer/bundle splitter, RENDER stage.
 * Built 2026-08-31. Loaded `defer` in <body>, after np-offer-assign.js has
 * already run (assign.js is synchronous in <head> and sets window.NP_OFFER
 * before this file's script tag is even parsed).
 *
 * ROOT-CAUSE FIX (2026-08-31, same day): the first build only ever ADDED a
 * panel above the real plan-group without hiding it, and the config it read
 * carried placeholder numbers — so a cookie-free scrape of ?offer=A..E all
 * came back with the SAME visible price text ($35/mo, $39, compare $117/
 * $234 from the untouched native block), even though window.NP_OFFER itself
 * was assigning correctly. Two fixes, both in this file: (1) the config now
 * carries the REAL cell content from
 * brands/np-gummies/offers/test-offer-matrix-2026-08-31.md, and (2) once a
 * B-E panel is successfully inserted, the native head-price / plan-group /
 * sticky-buy bar are hidden for that render (see the `np-offer-live` class
 * toggled below + assets/np-offer.css) so the panel's numbers are the ONLY
 * price the visitor sees — not a second block stacked next to an unchanged
 * one.
 *
 * CONTROL PRESERVATION (brief item 4) — REVISED 2026-09-01 PM #2: slot A now
 * renders tiles too (a "native_plans" cell whose tiles only click the real
 * plan radios — see ready() below and np-offer-config.liquid). The original
 * note follows for history. Slot A WAS a NO-OP for this
 * file. The live np-buybox.liquid markup is NEVER edited for control or for
 * a visitor with JS/cookies disabled — the browser simply shows the real
 * markup that was already there, and this file adds nothing, hides nothing,
 * for variant A. The hide-native behaviour below only ever fires for a
 * successfully-inserted B-E panel.
 *
 * RENDER SOURCE (brief item 3): every visible fact in the injected panel —
 * headline, tile qty/price/discount, gift line, shipping line — comes from
 * the JSON block in snippets/np-offer-config.liquid (id="np-offer-config").
 * Editing an offer is editing that one JSON file. Nothing here hardcodes
 * offer content.
 *
 * CART-PRICE-MATCHES-DISPLAY (2026-08-31 PM bundle-variants build — the
 * "display-layer only" era is OVER): every B-E tile in
 * np-offer-config.liquid now carries a "variant_id" pointing at a REAL
 * variant on hidden bundle product 9611154522370 whose Admin price IS the
 * tile's displayed price ($78/$156/$89/$29/$69/$117 — verified carting to
 * the cent, cookie-free, 2026-08-31). Clicking a tile (and the initial
 * default-tile pre-select) calls applyTile(), which writes that id into
 * the buybox's hidden /cart/add form (input[data-plan-id]), clears
 * selling_plan (bundle variants carry no plans), and stamps
 * properties[Plan] = "Offer <cell>" for attribution. The native submit
 * button + np.js's data-ajax-cart FormData handler then post it verbatim,
 * so the charged total structurally equals the displayed price. Quantity
 * stays 1 — the variant price covers the whole pack; never multiply by
 * bottle count. Legacy path: a tile WITHOUT variant_id still falls back to
 * clicking the real plan radio named by "maps_to_plan" (no current tile
 * uses it).
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function readConfig() {
    var el = document.getElementById('np-offer-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function money(n) {
    return n;
  }

  /* Write the tile's REAL purchase target into the buybox's hidden
   * /cart/add form. Primary path: tile.variant_id — the hidden bundle
   * variant that charges EXACTLY the displayed price (variant map lives in
   * np-offer-config.liquid). Fail-safe: if the form's id input is missing
   * (buybox markup changed upstream), do NOTHING rather than let a radio
   * fallback silently charge a different price than the tile displays.
   * Legacy path: a tile without variant_id clicks the real plan radio
   * named by maps_to_plan, reusing np.js's handler verbatim. */
  function applyTile(tile, box, cell) {
    if (tile.variant_id) {
      var vid = box.querySelector('input[data-plan-id]');
      if (!vid) return false;
      vid.value = tile.variant_id;
      var spi = box.querySelector('input[data-plan-sp]');
      if (spi) spi.value = '';
      var prop = box.querySelector('input[data-plan-prop]');
      if (prop) prop.value = 'Offer ' + cell;
      return true;
    }
    var real = box.querySelector('.plan[data-plan="' + (tile.maps_to_plan || '').replace(/"/g, '\\"') + '"]');
    if (real) {
      /* 2026-09-12 LOAD-FLASH ROOT CAUSE (Jared: "the site when loading does a
         sort of load, skip, then load"). This used to be `real.click()`.
         `.plan` is a <label>, and HTMLElement.click() on a <label> runs the
         label's ACTIVATION behaviour: it focuses the associated
         <input.plan-radio>. Focusing an element the browser cannot see
         scrolls it into view, so on every home-page load, at DOMContentLoaded
         (this file is `defer`), the viewport jumped from the hero (scrollY 0)
         down to the buy box (scrollY ~5370 desktop / ~5363 mobile) with no
         scrollTo/scrollIntoView anywhere in the stack, then the browser's own
         load-time scroll (fragment or reload restoration) yanked it back.
         Measured cookie-free with a focusin hook: stack = applyTile ->
         renderVariant -> ready, activeElement = input.plan-radio, scrollY 5370.
         Preferred path: np.js exports the selector as window.NPSelectPlan and
         we call it directly - no event at all. Fallback only if that export is
         missing: a plain Event('click') still reaches np.js's document-level
         `closest('.plan')` selector handler (it only checks the event TYPE),
         but per the DOM spec activation behaviour only runs for a MouseEvent
         'click', so the radio is never focused and the page never moves. Do
         NOT change this back to .click()/MouseEvent, and do not "fix" it with
         a scrollTo(0,0) after the click - that hides the symptom and still
         steals keyboard focus on load. */
      if (typeof window.NPSelectPlan === 'function') {
        window.NPSelectPlan(real); // np.js's own selector routine, no event, no focus
      } else {
        real.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
      }
      return true;
    }
    return false;
  }

  /* 2026-09-01 PM tile REWORK (Jared: mimic ref_offer_to_mimic.png's
   * persuasion layout, NP-branded): every tile is a horizontal card —
   *   banner  : bold deal text for THIS tile (+ "Retail $X/bottle" + a
   *             FREE SHIPPING / "+ $6.85 shipping" tag that is COMPUTED from
   *             cfg.free_ship_min, never hand-typed)
   *   body    : check circle · fanned bottle composite with a round SAVE $X
   *             badge · qty / price with retail struck through /
   *             "same as $X.XX/bottle" (COMPUTED price ÷ qty) / perks /
   *             Select ↔ Selected ✓ affordance
   * The 1-bottle tile renders through the SAME function with the same
   * weight (it just has no badge/compare) — see the buy-once reversal note in
   * snippets/np-offer-config.liquid. */
  function priceNum(str) {
    var m = /\$\s*([\d,]+(?:\.\d+)?)/.exec(str || '');
    return m ? parseFloat(m[1].replace(/,/g, '')) : NaN;
  }
  function fmt(n) {
    return '$' + n.toFixed(2);
  }
  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function buildTile(tile, box, isDefault, cell, root) {
    var qty = tile.qty || 1;
    var price = priceNum(tile.price);
    var compare = priceNum(tile.compare);
    var freeMin = root && typeof root.free_ship_min === 'number' ? root.free_ship_min : Infinity;
    // "free_ship": true is a per-tile override for a plan whose free shipping
    // is a PLAN perk rather than a cart-total threshold (the control's
    // subscription, whose native perk list has always said "Free shipping on
    // every order"). Everything else stays computed from free_ship_min.
    var freeShip = tile.free_ship === true || (!isNaN(price) && price >= freeMin);

    var btn = el('button', 'np-offer-tile' + (tile.highlight ? ' np-offer-tile--hl' : '') + (isDefault ? ' np-offer-tile--active' : ''));
    btn.type = 'button';
    btn.setAttribute('data-np-offer-tile', tile.maps_to_plan || '');
    btn.setAttribute('data-qty', String(qty));
    if (tile.variant_id) btn.setAttribute('data-variant-id', String(tile.variant_id));
    btn.setAttribute('aria-pressed', isDefault ? 'true' : 'false');

    /* banner */
    var banner = el('div', 'np-offer-tile-banner');
    banner.appendChild(el('span', 'np-offer-tile-deal', tile.deal || tile.qty_label || (qty === 1 ? '1 Bottle' : qty + ' Bottles')));
    if (!isNaN(compare) && compare > 0) {
      banner.appendChild(el('span', 'np-offer-tile-retail', 'Retail ' + fmt(compare / qty) + '/bottle'));
    }
    var ship = el('span', 'np-offer-tile-ship' + (freeShip ? ' np-offer-tile-ship--free' : ''),
      freeShip ? 'FREE SHIPPING' : ((root && root.ship_paid_label) || ''));
    if (ship.textContent) banner.appendChild(ship);
    btn.appendChild(banner);

    /* body */
    var body = el('div', 'np-offer-tile-body');
    var check = el('span', 'np-offer-tile-check');
    check.setAttribute('aria-hidden', 'true');
    body.appendChild(check);

    var vis = el('div', 'np-offer-tile-vis');
    if (tile.img) {
      var img = document.createElement('img');
      img.className = 'np-offer-tile-img';
      img.src = tile.img;
      img.alt = qty + (qty === 1 ? ' bottle' : ' bottles') + ' of Natures Relax Gummies';
      img.loading = 'lazy';
      vis.appendChild(img);
    }
    var badgeText = tile.badge || tile.discount_label ||
      (!isNaN(compare) && !isNaN(price) && compare > price ? 'SAVE $' + Math.round(compare - price) : '');
    if (badgeText) vis.appendChild(el('span', 'np-offer-tile-badge', badgeText));
    body.appendChild(vis);

    var pricing = el('div', 'np-offer-tile-pricing');
    if (tile.flag) pricing.appendChild(el('span', 'np-offer-tile-flag', tile.flag));
    pricing.appendChild(el('div', 'np-offer-tile-qty', qty === 1 ? '1 Bottle' : qty + ' Bottles'));
    var pr = el('div', 'np-offer-tile-price');
    pr.appendChild(el('span', 'np-offer-tile-price-now', tile.price));
    if (tile.price_suffix) pr.appendChild(el('span', 'np-offer-tile-price-suffix', tile.price_suffix));
    if (tile.compare) pr.appendChild(el('s', 'np-offer-tile-compare', tile.compare));
    pricing.appendChild(pr);
    // "same as $X.XX/bottle" — computed from the REAL price ÷ bottle count,
    // which is the same number the cart charges (variant price covers the
    // whole pack). A single just says what it is.
    pricing.appendChild(el('div', 'np-offer-tile-per',
      tile.per || (qty > 1 && !isNaN(price) ? 'Same as ' + fmt(price / qty) + '/bottle' : '30-day supply')));
    if (tile.perks) pricing.appendChild(el('div', 'np-offer-tile-perks', '🎁 ' + tile.perks));
    pricing.appendChild(el('span', 'np-offer-tile-cta', isDefault ? 'Selected ✓' : 'Select'));
    body.appendChild(pricing);
    btn.appendChild(body);

    btn.addEventListener('click', function () {
      applyTile(tile, box, cell);
      var group = btn.parentElement;
      if (group) {
        Array.prototype.forEach.call(group.querySelectorAll('.np-offer-tile'), function (t) {
          t.classList.remove('np-offer-tile--active');
          t.setAttribute('aria-pressed', 'false');
          var c = t.querySelector('.np-offer-tile-cta');
          if (c) c.textContent = 'Select';
        });
      }
      btn.classList.add('np-offer-tile--active');
      btn.setAttribute('aria-pressed', 'true');
      var cta = btn.querySelector('.np-offer-tile-cta');
      if (cta) cta.textContent = 'Selected ✓';
      // legacy: a text-link buy-once (no longer configured) would deselect here
      var pan = btn.closest('.np-offer-panel');
      var bo = pan && pan.querySelector('.np-offer-buyonce--active');
      if (bo) bo.classList.remove('np-offer-buyonce--active');
    });

    return btn;
  }

  function renderVariant(variant, cfg, box, root) {
    var panel = document.createElement('div');
    panel.className = 'np-offer-panel';
    panel.setAttribute('data-np-offer-panel', variant);

    // 2026-09-01 (Jared screenshot report): this panel used to always render
    // a hardcoded "Test offer · <letter>" QA label as the first thing a real
    // customer saw — a debug leftover from the 2026-08-31 build that never
    // got stripped before the panel went live. Removed outright; there is no
    // config-driven replacement (no `badge` key in np-offer-config.liquid),
    // so nothing else renders in its place. `data-np-offer-panel` above still
    // carries the variant for QA/automation.

    if (cfg.headline) {
      var h = document.createElement('p');
      h.className = 'np-offer-panel-headline';
      h.textContent = cfg.headline;
      panel.appendChild(h);
    }

    var defaultIdx = typeof cfg.default_tile === 'number' ? cfg.default_tile : 0;
    var defaultTile = null;
    if (Array.isArray(cfg.tiles) && cfg.tiles.length) {
      var grid = document.createElement('div');
      grid.className = 'np-offer-tile-grid';
      cfg.tiles.forEach(function (tile, i) {
        var isDefault = i === defaultIdx;
        if (isDefault) defaultTile = tile;
        grid.appendChild(buildTile(tile, box, isDefault, variant, root));
      });
      panel.appendChild(grid);
    }

    if (cfg.gift_line) {
      var g = document.createElement('p');
      g.className = 'np-offer-panel-line';
      g.textContent = '🎁 ' + cfg.gift_line;
      panel.appendChild(g);
    }

    if (cfg.shipping_line) {
      var s = document.createElement('p');
      s.className = 'np-offer-panel-line';
      s.textContent = '🚚 ' + cfg.shipping_line;
      panel.appendChild(s);
    }

    /* LEGACY — buy_once text link (2026-09-01 AM). SUPERSEDED the same day:
     * Jared wants a real 1-bottle TILE on every cell, so np-offer-config no
     * longer sets buy_once and this block renders nothing. Kept only so an
     * old config with the key still degrades gracefully.
     * Original note (2026-09-01, Jared item 5): the offer matrix (cell B) sells
     * the 1-bottle single via a native "Buy once · $39" line BELOW the panel
     * tiles — but np-offer-live hides the whole native plan-group, so the
     * single silently vanished on cell B. This config-driven line restores
     * it: clicking wires the hidden /cart/add form to the CONTROL product's
     * real One-Time Purchase variant (full price, no incentive — exactly why
     * it doesn't belong among the offer tiles). Cells without "buy_once" in
     * config (C/D/E) render nothing here — E's missing single is DESIGN
     * (min-quantity trick), not a bug. */
    if (cfg.buy_once && cfg.buy_once.variant_id) {
      var boBtn = document.createElement('button');
      boBtn.type = 'button';
      boBtn.className = 'np-offer-buyonce';
      boBtn.textContent = cfg.buy_once.label || 'Buy once';
      boBtn.addEventListener('click', function () {
        var ok = applyTile({ variant_id: cfg.buy_once.variant_id }, box, variant + ' single');
        if (!ok) return;
        Array.prototype.forEach.call(panel.querySelectorAll('.np-offer-tile'), function (t) {
          t.classList.remove('np-offer-tile--active');
        });
        boBtn.classList.add('np-offer-buyonce--active');
      });
      panel.appendChild(boBtn);
    }

    // Insert as a sibling BEFORE the real plan-group — the real
    // plan-group/radio/form markup underneath is never edited or removed,
    // only hidden via CSS class (see np-offer.css `html.np-offer-live`)
    // once insertion succeeds below, so it still exists for /cart/add.
    var mountBefore = box.querySelector('[data-np-offer-mount]') ||
      box.querySelector('.plan-group');
    if (mountBefore && mountBefore.parentNode) {
      mountBefore.parentNode.insertBefore(panel, mountBefore);
    } else {
      // No known anchor found (theme markup changed upstream) — fail safe by
      // not inserting anything rather than guessing a location. Control
      // experience (item 4) always wins over showing the test panel.
      return false;
    }

    // Wire the hidden native form to the default/highlighted tile's real
    // purchase target immediately, so a visitor who taps the buy button
    // without touching a tile still gets charged the price the highlighted
    // tile displays.
    if (defaultTile) {
      applyTile(defaultTile, box, variant);
    }

    return true;
  }

  ready(function () {
    var box = document.querySelector('[data-buybox]');
    if (!box) return; // not the PDP — nothing to do

    var variant = window.NP_OFFER || 'A';
    document.documentElement.setAttribute('data-np-offer', variant); // QA/verification marker, all variants incl. A

    var cfg = readConfig();
    if (!cfg || !cfg[variant] || cfg[variant].control || !Array.isArray(cfg[variant].tiles)) {
      // Config missing/unparseable, an unknown slot, a cell explicitly marked
      // "control": true, or a cell with no tiles: DO NOTHING. The real
      // np-buybox.liquid markup already rendered the live default plans; this
      // is the no-JS/no-cookie fallback path.
      return;
    }

    var inserted = renderVariant(variant, cfg[variant], box, cfg);
    if (inserted && cfg[variant].native_plans) {
      // 2026-09-01 PM #2 (Jared): the CONTROL / organic default (slot A) gets
      // the same tile cards as B-E, but its tiles carry maps_to_plan only —
      // every click lands on the REAL plan radio via np.js's handler, so the
      // control's variants, selling plan, head price, submit label and native
      // sticky-buy bar all keep working exactly as before. Only the native
      // .plan-group is hidden (html.np-offer-native, see np-offer.css); the
      // np-offer-live class is deliberately NOT added here, so np-offer-cta.js
      // stays a no-op and the control is restyled, never re-priced.
      document.documentElement.classList.add('np-offer-native');
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'np_offer_rendered', np_offer: variant, np_offer_native: true });
    } else if (inserted) {
      // Hide the native head-price / plan-group / sticky-buy bar for this
      // render only — see assets/np-offer.css. Never touches the DOM nodes
      // themselves (no display:none set inline, no removal), purely a class
      // toggle, so nothing here is a Cell-A/control markup edit and the
      // real form + its default field values remain intact for /cart/add.
      document.documentElement.classList.add('np-offer-live');
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'np_offer_rendered', np_offer: variant });
    }
  });
})();
